/* eslint-disable */
//TODO(nibeauli): Remove this once we get the OneShell sdk integrated correctly.
/**
 * ---------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * ---------------------------------------------------------------------------------------------
 */

import React, { useContext, useEffect, useId } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { makeDocsLink } from '../../constants/Links';
import { Flex } from '@tdp/common/fluentV9MigrationComponents';
import { makeStyles } from '@fluentui/react-components';
import { useCommand, useSelector } from '@tdp';
import { DeveloperPortalRootContext } from '@tdp/core/TdpServicesContext';
import { loadBuildConfig } from '@tdp/core/util/buildConfig';
import { ToggleClientSwitchEnabled } from './ToggleLeftRegularSVG';
import { ILogger } from '@tdp/common/telemetry';

export const HeaderData = {
  O365ShellHeaderId: 'O365ShellHeaderId',
  PostLogoutRedirectUri: 'post_logout_redirect_uri',
  AadLogoutUrl: 'https://login.microsoftonline.com/common/oauth2/logout',
  AppHeaderLinkText: 'Developer Portal',
};

const useStyle = makeStyles({
  HeaderStyles: {
    minHeight: '50px',
    background: '#484644',
    flex: '0 0 50px',
    width: '100vw',
  },
});

const Links = {
  Help: makeDocsLink('microsoftteams/platform/mstdd-landing'),
  oneShellHeaderBootstrapperLink:
    'https://shell.cdn.office.net/api/shellbootstrapper/consumer/oneshell',
};

// declaring globals which must be any
declare const window: any;
declare const self: any;
const REACT_APP_OCV_APP_ID = 2538;
const OCV_PPE_APP_ID = 50045;

export const Header: React.FC = () => {
  const O365ShellHeaderId = HeaderData.O365ShellHeaderId;

  const o365NavbarData = useSelector((state) => state.header.headerData) as any;

  const intl = useIntl();
  const classes = useStyle();
  const getHeaderData = useCommand(
    (commands) => commands.headerCommands.getHeaderData
  );

  const handleClientSwitch = useCommand(
    (commands) => commands.clientPreferenceCommands.changeUserClientPreference);

  const { services: { logger } } = useContext(DeveloperPortalRootContext);
  useEffect(() => {
    const oneShellscriptElement = document.createElement('script');
    oneShellscriptElement.src = Links.oneShellHeaderBootstrapperLink;
    const oneShellScriptLoadCallback = () => {
      getHeaderData();
    };
    oneShellscriptElement.onload = oneShellScriptLoadCallback;
    oneShellscriptElement.onerror = (error) => {
      logger.logUserBi({ telemetryMarker: "oneshell-load-error ", message: `Failed to load O365 shell header with error: ${error.toString()}` });

    };
    document.body.appendChild(oneShellscriptElement);
  }, []);

  let o365ShellUsed = false;
  if (
    o365NavbarData &&
    Object.keys(o365NavbarData).length > 0 &&
    o365NavbarData.constructor === Object &&
    window.O365Shell
  ) {
    // temporarily disabling the header for localhost
    // todo: remove this when we are ready to use the header on localhost
    if (window.location.origin.includes('localhost')) {
      o365ShellUsed = false;
    } else {
      o365ShellUsed = true;
    }
  }

  const renderShellHeader = (): JSX.Element => {
    const postLogoutRedirectUriKey = HeaderData.PostLogoutRedirectUri;
    const clientData = JSON.parse(o365NavbarData.ClientData);
    const { Ocv } = loadBuildConfig();
    const ocvAppId = Ocv?.appId ?? REACT_APP_OCV_APP_ID;
    const postLogoutRedirectUri = encodeURIComponent(
      `${window.location.origin}`
    );
    const aadlogoutUrl = HeaderData.AadLogoutUrl;
    clientData.AppHeaderLinkText = HeaderData.AppHeaderLinkText;
    window.O365Shell.SetData({
      ...o365NavbarData,
      ClientData: JSON.stringify({
        ...clientData,
        AppHeaderLinkText: HeaderData.AppHeaderLinkText,
      }),
      SignOutLink: {
        ...o365NavbarData.SignOutLink,
        Url: `${aadlogoutUrl}?${postLogoutRedirectUriKey}=${postLogoutRedirectUri}`,
      },
    });
    addFeedbackButton(intl, logger);
    addClientSwitcherButton(intl, logger, handleClientSwitch,);
    window.O365Shell.RenderAsync(
      {
        top: O365ShellHeaderId,
        isThinHeader: true,
        disableDelayLoad: true,
        OnHelpButtonClick: () => onHelpButtonClick(),
      },
      renderedCallback,
      loadedCallback,
      loadFailureCallback
    );
    window.O365Shell.OnSignOut(onSignOut);
    window.O365Shell.NFD.OnFeedbackMetadataRequested(
      (
        feedbackMetadata: any,
        provideScreenshot: (base64Screenshot: string) => void
      ) => {
        self?.windowService?.nativeWindow?.OfficeBrowserFeedback?.html2canvas(
          self?.documentService?.nativeDocument?.body,
          {
            background: '#ffffff',
          }
        )().then((canvas: HTMLCanvasElement) => {
          provideScreenshot &&
            provideScreenshot(canvas?.toDataURL('image/jpeg'));
        });

        feedbackMetadata.options.appId = +ocvAppId;
        feedbackMetadata.options.environment =
          +ocvAppId! === OCV_PPE_APP_ID ? 1 : 0;
        return feedbackMetadata;
      }
    );

    return <> </>;
  };

  /*
   * Wire up the Support Central with the shell's help button based on the following criteria
   * 1. Support Central flight should be enabled.
   * 2. Support Central should have proper permission.
   * 3. Support Central should be enabled on the current path.
   * 4. Support Central should be loaded successfully.
   */
  const onHelpButtonClick = (): boolean => {
    // const enableSupportCentral = getDevCenterFeature(TeamsDevCenterFeatureFlagNames.EnableSupportCentral);
    const supportCentral = window.SupportCentral;
    // Todo: if possible check permissions - Brook
    const hasPermission = true;

    if (!hasPermission || !supportCentral || !supportCentral.ToggleWidget) {
      // Fallback to default shell help experience.
      window.open(Links.Help, '_blank');
      return true;
    } else {
      /*
       * 'TeamsDevPortal' is logged in support central logs and
       * can be used to provide usage metrics if needed.
       */
      return supportCentral.ToggleWidget('TeamsDevPortal');
    }
  };

  const onSignOut = () => {
    // TODO: add signOut functionality when its ready.
    // ApsAuthClient.logout();
    // Logger.LogInfo(TelemetryEventName.SignOut, Strings.appShellSignOUt);
  };

  const renderedCallback = () => {
    // TODO: Add rendered logs
    logger.logUserBi({ telemetryMarker: "api-action-get-header-shell-info-render-success", message: "O365 shell header rendered successfully." });
  };

  const loadedCallback = () => {
    // TODO: add loaded logs
    logger.logUserBi({ telemetryMarker: "api-action-get-header-shell-info-loaded-success", message: "O365 shell header loaded successfully." });
  };

  const loadFailureCallback = (error: any) => {
    // Add failure logs
    // Logger.LogError(TelemetryEventName.GetHeaderShellInfoLoadFailure, errorMsg);
    logger.logUserBi({ telemetryMarker: "api-action-get-header-shell-info-load-error", message: `O365 shell header load failed with error: ${error.toString()}` });
  };

  return (
    <>
      {o365ShellUsed ? (
        <Flex className={classes.HeaderStyles} id={O365ShellHeaderId}>
          {renderShellHeader()}
        </Flex>
      ) : (
        <Flex className={classes.HeaderStyles} />
      )}
    </>
  );
};

/** Feedback button */
const addFeedbackButton = (intl: IntlShape, logger: ILogger) => {
  const buttonId = 'GiveFeedbackButton';
  const feedbackProviderId = 'Feedback';

  const onHide: () => void = () => {
    window.O365Shell.FlexPane?.CloseFlexPaneForProvider(feedbackProviderId);
  };

  const onShow: () => void = () => {
    if (window.O365Shell.FlexPane) {
      logger.logUserBi({ telemetryMarker: "header-action-feedback-button-clicked", message: "User clicked on feedback button." });
      window.O365Shell.FlexPane.OpenFlexPaneForProvider(feedbackProviderId);
    }
  };

  window.O365Shell.Extensibility?.AddOrUpdateCustomHeaderButton({
    ariaLabel: intl.formatMessage({
      defaultMessage: 'Feedback',
      description: 'feedback button',
    }),
    iconFontName: '\ue76e',
    id: buttonId,
    isStateless: true,
    onShow,
    onHide,
  });
};

const addClientSwitcherButton = (intl: IntlShape, logger: ILogger, handleClientSwitch: (isProduction: boolean) => void) => {
  const envConfig = loadBuildConfig();
  const buttonText = intl.formatMessage({ description: "Button text to show new developer portal is enabled", defaultMessage: "New Developer Portal" });
  const buttonAriaLabel = intl.formatMessage({ description: "Button text to show new developer portal is enabled", defaultMessage: "You are on new Developer Portal experience. Use the toggle to switch to the previous experience." });
  const isProduction = envConfig.api.baseUrl === 'https://dev.teams.microsoft.com/api';
  const onShow = () => {
    handleClientSwitch(isProduction);
  }

  // TODO: Rolback step 4 - Line 251-  !isProduction && window.O365Shell.Extensibility?.AddOrUpdateCustomHeaderButton({
  // When rolling back we want to rollback only for Prod so toggle client switch should be disabled in PROD like this.
  window.O365Shell.Extensibility?.AddOrUpdateCustomHeaderButton({
    ariaLabel: buttonAriaLabel,
    id: "clientSwitcherButtonToggle",
    isStateless: true,
    onShow,
    iconData: {
      element: <ToggleClientSwitchEnabled />
    },
  },
    {
      ariaLabel: buttonAriaLabel,
      id: "clientSwitcherButtonText",
      isStateless: true,
      isNoninteractive: true,
      buttonText
    }
  );
};

